import React, { Fragment, useState, useEffect, useRef } from 'react'
import { Tab, Nav } from 'react-bootstrap'
import { gql, useQuery } from '@apollo/client'
import Tooltip from '../common/Tooltip'
import Flatpickr from 'react-flatpickr'
import 'flatpickr/dist/flatpickr.css'
import Notification from '../../components/partials/Notification'
import Card from '../common/Card'
import HeaderTable from './HeaderTable'
import { CubeLoader } from '../../layouts/components/CubeLoader'
import { CalendarIcon } from '../icons'

const GET_HEADERS = gql`
    query Campaigns {
        campaigns {
            headers
            subheaders
            lastExtractedAt
        }
    }
`

const ExtractionReport = () => {
    const campaign = useQuery(GET_HEADERS)
    const fp = useRef(null)
    const [selectedDate, setSelectedDate] = useState(null)
    const [tmDates, setTmDates] = useState([])

    const extractions = () => {
        let headers, subheaders
        try {
            headers = campaign.data.campaigns[0]?.headers ? JSON.parse(campaign.data.campaigns[0]?.headers) : null
        } catch (error) {
            headers = campaign.data.campaigns[0]?.headers
        }
        try {
            subheaders = campaign.data.campaigns[0]?.subheaders
                ? JSON.parse(campaign.data.campaigns[0]?.subheaders)
                : null
        } catch (error) {
            subheaders = campaign.data.campaigns[0]?.subheaders
        }

        return {
            headers: headers,
            subheaders: subheaders,
        }
    }

    const date = () => {
        const data = extractions()
        try {
            let date = data.headers.find((item) => item.date === selectedDate)['date']
            const dateParts = date.split('/')
            date = new Date(`${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`)
            return new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }).format(date)
        } catch (error) {
            const date = campaign.data.campaigns[0].lastExtractedAt
            return campaign.data.campaigns[0]?.lastExtractedAt
                ? new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }).format(
                      new Date(date),
                  )
                : null
        }
    }

    const headlines = () => {
        const data = extractions()
        try {
            return data.headers.find((item) => item.date === selectedDate)['array']?.filter(Boolean) ?? []
        } catch (error) {
            return []
        }
    }

    const subheadlines = () => {
        const data = extractions()
        try {
            return data.subheaders.find((item) => item.date === selectedDate)['array']?.filter(Boolean) ?? []
        } catch (error) {
            return []
        }
    }

    const handleDateChange = (selectedDates, dateStr, instance) => {
        const dateParts = dateStr.split('-')
        const formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`
        setSelectedDate(formattedDate)
    }

    useEffect(() => {
        if (tmDates.length === 0) {
            let date, dates
            try {
                date = campaign.data.campaigns[0]?.headers
                    ? JSON.parse(campaign.data.campaigns[0]?.headers).pop()['date']
                    : null
                dates = campaign.data.campaigns[0]?.headers
                    ? JSON.parse(campaign.data.campaigns[0].headers)[0].date
                        ? JSON.parse(campaign.data.campaigns[0]?.headers).map((item) => item.date)
                        : []
                    : []
            } catch (error) {
                date = null
                dates = []
            }

            if (date) {
                setSelectedDate(date)
            }

            if (dates) {
                dates = dates.map((item) => {
                    const dateParts = item.split('/')
                    return `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`
                })

                setTmDates(dates)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- tmDates is derived state; we only want to run when campaign/selectedDate change
    }, [campaign, selectedDate])

    return (
        <Fragment>
            <Card className="card-block">
                {campaign.loading ? (
                    <CubeLoader size="sm" fullScreen={false} />
                ) : (
                    <Fragment>
                        <Card.Header className="card-header p-4 flex-column">
                            <div className="d-flex justify-content-between align-items-center">
                                <h6 className="card-title">Time Machine</h6>
                                {date() && (
                                    <div className="d-flex align-items-center gap-1">
                                        <button
                                            type="button"
                                            className="border-0 bg-transparent p-0 d-flex align-items-center text-blue-300"
                                            onClick={() => fp.current?.flatpickr?.open()}
                                            aria-label="Open date picker"
                                        >
                                            <CalendarIcon size={20} />
                                        </button>
                                        <Flatpickr
                                            ref={fp}
                                            value={
                                                selectedDate ? selectedDate.split('/').reverse().join('-') : undefined
                                            }
                                            options={{ enable: tmDates }}
                                            className="hidden-input"
                                            onChange={handleDateChange}
                                        />
                                        <span className="text-blue-300">{date()}</span>
                                        <Tooltip
                                            text={
                                                'Click on the Calendar icon to select different date to see other Extractions'
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                            <span className="small text-white">Generative AI Extraction</span>
                        </Card.Header>
                        <Card.Body className="card-thumbnail p-0">
                            {date() ? (
                                <Tab.Container defaultActiveKey="first">
                                    <div className="tab-bottom-bordered">
                                        <Nav
                                            className="mb-0 nav-tabs justify-content-start d-flex mx-4"
                                            id="nav-tab1"
                                            role="tablist"
                                        >
                                            <Nav.Link
                                                className="py-2 px-4 text-white"
                                                eventKey="first"
                                                data-bs-toggle="tab"
                                                data-bs-target="#header-1"
                                                type="button"
                                                role="tab"
                                                aria-controls="header-1"
                                                aria-selected="false"
                                            >
                                                Headline
                                            </Nav.Link>
                                            <Nav.Link
                                                className="py-2 px-4 text-white"
                                                eventKey="second"
                                                data-bs-toggle="tab"
                                                data-bs-target="#header-2"
                                                type="button"
                                                role="tab"
                                                aria-controls="header-2"
                                                aria-selected="false"
                                            >
                                                Primary Text
                                            </Nav.Link>
                                        </Nav>
                                    </div>
                                    <Tab.Content className="iq-tab-fade-up" id="nav-tabContent">
                                        <Tab.Pane
                                            eventKey="first"
                                            id="header-1"
                                            role="tabpanel"
                                            aria-labelledby="header-1"
                                        >
                                            <HeaderTable headers={headlines()} />
                                        </Tab.Pane>
                                        <Tab.Pane
                                            eventKey="second"
                                            id="header-2"
                                            role="tabpanel"
                                            aria-labelledby="header-2"
                                        >
                                            <HeaderTable headers={subheadlines()} />
                                        </Tab.Pane>
                                    </Tab.Content>
                                </Tab.Container>
                            ) : (
                                <CubeLoader size="sm" fullScreen={false} />
                            )}
                        </Card.Body>
                    </Fragment>
                )}
            </Card>
            <Notification />
        </Fragment>
    )
}

export default ExtractionReport
